
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { assertIsFn, AddContext } from "@storybook/addon-docs";

import { Canvas, Meta, Story } from '@storybook/addon-docs';
import singleDemo from './demo/single.twig';
import multipleDemo from './demo/multiple.twig';
const defaultArgs = {
  content_class: 'o-rhythm',
  object_shape: 'circle',
  example_paragraphs: 3,
  example_object_img_src: '/media/feature-ozzie-wide.jpg',
  example_object_img_size: 480,
};
const demoStory = (args, multiple) => {
  args = { ...defaultArgs, ...args };
  if (args.object_shape === 'square') {
    delete args.object_shape;
  }
  return multiple ? multipleDemo(args) : singleDemo(args);
};
const singleTransformSource = (_src, storyContext) => {
  const args = storyContext.args || storyContext.initialArgs || {};
  if (args.object_shape === 'square') {
    delete args.object_shape;
  }
  for (const key of Object.keys(args)) {
    if (key.startsWith('example_')) {
      delete args[key];
    }
  }
  const argsString =
    Object.keys(args).length > 0
      ? ` with ${JSON.stringify(args, null, 2)}`
      : '';
  return `{% embed '@cloudfour/objects/hype-group/hype-group.twig'${argsString} only %}
  {% block intro %}
    {# heading, etc. #}
  {% endblock %}
  {% block object %}
    {# image element #}
  {% endblock %}
  {% block content %}
    {# description, etc. #}
  {% endblock %}
{% endembed %}`;
};


const layoutProps = {
  
};
const MDXLayout = "wrapper"
function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Meta title="Objects/Hype Group" parameters={{
      layout: 'fullscreen',
      docs: {
        inlineStories: false
      }
    }} argTypes={{
      reverse: {
        type: 'boolean'
      },
      object_shape: {
        type: 'select',
        options: ['circle', 'square']
      },
      object_outline: {
        type: 'boolean'
      },
      class: {
        type: 'string'
      },
      intro_class: {
        type: 'string'
      },
      content_class: {
        type: 'string'
      },
      example_object_img_src: {
        type: 'string'
      },
      example_object_img_size: {
        type: 'number'
      },
      example_paragraphs: {
        type: 'number',
        control: {
          type: 'range',
          min: 1,
          max: 4,
          step: 1
        }
      }
    }} mdxType="Meta" />
    <h1>{`Hype Group`}</h1>
    <p>{`The Hype Group layout object is used to display promotional or marketing content highlighting a specific feature or specialty. The visual object therein will float or overlap in a way that appears organic and spontaneous. It's an opinionated layout that works best with a square image or illustration and multiple, alternating content blocks of comparable length.`}</p>
    <p>{`For best results, Hype Groups should be contained inside of a standard-width `}<a parentName="p" {...{
        "href": "/docs/objects-container--basic"
      }}>{`Container object`}</a>{`.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Single" height="500px" args={defaultArgs} parameters={{
        docs: {
          transformSource: singleTransformSource
        }
      }} mdxType="Story">
    {args => demoStory(args)}
  </Story>
    </Canvas>
    <h2>{`Object options`}</h2>
    <ul>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`object_shape`}</inlineCode>{` template property or `}<inlineCode parentName="li">{`o-hype-group--{shape}-object`}</inlineCode>{` modifier class can be used to change the shape of an object. The default square shape is best for screenshots or interface details, while the `}<inlineCode parentName="li">{`circle`}</inlineCode>{` is best for photographs or illustrations.`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`object_outline`}</inlineCode>{` template property or `}<inlineCode parentName="li">{`o-hype-group--outline-object`}</inlineCode>{` modifier class will apply a subtle inset border to the object. This can be helpful for differentiating lighter backgrounds from the surrounding page.`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`reverse`}</inlineCode>{` template property or `}<inlineCode parentName="li">{`o-hype-group--reverse`}</inlineCode>{` modifier class will arrange the visual object on the opposite side at larger viewport sizes. (It will remain the same at smaller sizes to preserve a consistent reading line for text content.)`}</li>
    </ul>
    <p>{`This example shows a square shaped object with an outline and reversed at larger viewport sizes:`}</p>
    <Canvas mdxType="Canvas">
  <Story name="With options" height="500px" args={{
        ...defaultArgs,
        object_shape: 'square',
        object_outline: true,
        reverse: true
      }} parameters={{
        docs: {
          transformSource: singleTransformSource
        }
      }} mdxType="Story">
    {args => demoStory(args)}
  </Story>
    </Canvas>
    <h2>{`Multiple Hype Groups`}</h2>
    <p>{`Hype Groups work best when there's more than one per page. This example shows multiple Hype Groups with the `}<inlineCode parentName="p">{`reverse`}</inlineCode>{` property alternating. The containers also have alternating `}<inlineCode parentName="p">{`t-alternate`}</inlineCode>{` classes applied (see `}<a parentName="p" {...{
        "href": "/docs/design-themes--light"
      }}>{`Themes`}</a>{` for more info).`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Multiple" height="640px" args={{
        ...defaultArgs,
        example_object_img_src: '/media/avatar-buster-a.jpg'
      }} mdxType="Story">
    {args => demoStory(args, true)}
  </Story>
    </Canvas>
    <h2>{`Template properties`}</h2>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`class`}</inlineCode>{` (string): A CSS class name to append to the main element's `}<inlineCode parentName="li">{`class`}</inlineCode>{` attribute.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`content`}</inlineCode>{` (block): The main content, usually two or three short paragraphs of descriptive content.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`content_class`}</inlineCode>{` (string): An additional class to append to the content container, for example `}<inlineCode parentName="li">{`o-rhythm`}</inlineCode>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`intro`}</inlineCode>{` (block): The heading or other introductory content.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`intro_class`}</inlineCode>{` (string): An additional class to append to the intro container.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`object`}</inlineCode>{` (block): An image or similar visual element. Ideally square in aspect ratio.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`object_outline`}</inlineCode>{` (boolean): If `}<inlineCode parentName="li">{`true`}</inlineCode>{`, the object will have an inner shadow. This is to help offset interface screenshots from the background.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`object_shape`}</inlineCode>{` (string): The shape of the object. If `}<inlineCode parentName="li">{`circle`}</inlineCode>{`, the image will be displayed in a circle.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`reverse`}</inlineCode>{` (boolean): If `}<inlineCode parentName="li">{`true`}</inlineCode>{`, the object will be displayed on the left/inline-start side of the content at larger viewport sizes.`}</li>
    </ul>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

export const single = (args => demoStory(args));
single.storyName = 'Single';
single.args = defaultArgs;
single.parameters = { storySource: { source: 'args => demoStory(args)' }, ...{
  docs: {
    transformSource: singleTransformSource
  }
} };

export const withOptions = (args => demoStory(args));
withOptions.storyName = 'With options';
withOptions.args = {
  ...defaultArgs,
  object_shape: 'square',
  object_outline: true,
  reverse: true
};
withOptions.parameters = { storySource: { source: 'args => demoStory(args)' }, ...{
  docs: {
    transformSource: singleTransformSource
  }
} };

export const multiple = (args => demoStory(args, true));
multiple.storyName = 'Multiple';
multiple.args = {
  ...defaultArgs,
  example_object_img_src: '/media/avatar-buster-a.jpg'
};
multiple.parameters = { storySource: { source: 'args => demoStory(args, true)' } };

const componentMeta = { title: 'Objects/Hype Group', parameters: {
  layout: 'fullscreen',
  docs: {
    inlineStories: false
  }
}, argTypes: {
  reverse: {
    type: 'boolean'
  },
  object_shape: {
    type: 'select',
    options: ['circle', 'square']
  },
  object_outline: {
    type: 'boolean'
  },
  class: {
    type: 'string'
  },
  intro_class: {
    type: 'string'
  },
  content_class: {
    type: 'string'
  },
  example_object_img_src: {
    type: 'string'
  },
  example_object_img_size: {
    type: 'number'
  },
  example_paragraphs: {
    type: 'number',
    control: {
      type: 'range',
      min: 1,
      max: 4,
      step: 1
    }
  }
}, includeStories: ["single","withOptions","multiple"],  };

const mdxStoryNameToKey = {"Single":"single","With options":"withOptions","Multiple":"multiple"};

componentMeta.parameters = componentMeta.parameters || {};
componentMeta.parameters.docs = {
  ...(componentMeta.parameters.docs || {}),
  page: () => <AddContext mdxStoryNameToKey={mdxStoryNameToKey} mdxComponentAnnotations={componentMeta}><MDXContent /></AddContext>,
};

export default componentMeta;